import { useDisclosure, useMultiStyleConfig } from '@chakra-ui/react-v2';
import { isRefObject } from '@chakra-ui/utils';
import { useCallback, useMemo, useRef } from 'react';
import type { OptionTypeBase } from 'react-select';

import { noop } from '@frontend/utils';

import { type SelectStyles } from './SelectStylesContext';
import { type SelectType } from './SelectType';
import { type SelectWrapperProps } from './SelectWrapper';
import {
  ClearIndicator,
  Control,
  DropdownIndicator,
  Group,
  GroupHeading,
  IndicatorSeparator,
  IndicatorsContainer,
  LoadingMessage,
  Menu,
  MenuList,
  MultiValueContainer,
  MultiValueLabel,
  MultiValueRemove,
  NoOptionsMessage,
  Option,
  Placeholder,
  SelectContainer,
  SingleValue,
  ValueContainer,
} from './subcomponents';
import type { BaseSelectProps, RenderSelectProps } from './types';
import { getInputElementFromSelectRefCallbackElement } from './utils';

export const useSelectComponents = <
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
  T extends SelectType,
>(
  components: RenderSelectProps<
    OptionType,
    IsMulti,
    T
  >['selectProps']['components'],
): RenderSelectProps<OptionType, IsMulti, T>['selectProps']['components'] => {
  const baseComponents: RenderSelectProps<
    OptionType,
    IsMulti,
    T
  >['selectProps']['components'] = useMemo(
    () => ({
      ClearIndicator,
      Control,
      DropdownIndicator,
      Group,
      GroupHeading,
      IndicatorSeparator,
      IndicatorsContainer,
      LoadingMessage,
      Menu,
      MenuList,
      MultiValueContainer,
      MultiValueLabel,
      MultiValueRemove,
      NoOptionsMessage,
      Option,
      Placeholder,
      SelectContainer,
      SingleValue,
      ValueContainer,
    }),
    [],
  );

  const combinedComponents: RenderSelectProps<
    OptionType,
    IsMulti,
    T
  >['selectProps']['components'] = useMemo(
    () => ({
      ...baseComponents,
      ...components,
    }),
    [baseComponents, components],
  );

  return combinedComponents;
};

export const useSelectStyles = <
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
  T extends SelectType,
>(
  styles: RenderSelectProps<OptionType, IsMulti, T>['selectProps']['styles'],
): RenderSelectProps<OptionType, IsMulti, T>['selectProps']['styles'] => {
  const baseStyles: RenderSelectProps<
    OptionType,
    IsMulti,
    T
  >['selectProps']['styles'] = useMemo(
    () => ({
      input: () => ({}),
      menu: () => ({}),
      loadingMessage: () => ({}),
      noOptionsMessage: () => ({}),
      multiValueRemove: () => ({}),
      control: () => ({}),
      menuList: () => ({}),
      option: () => ({}),
      multiValue: () => ({}),
      multiValueLabel: () => ({}),
      group: () => ({}),
    }),
    [],
  );

  const combinedStyles: RenderSelectProps<
    OptionType,
    IsMulti,
    T
  >['selectProps']['styles'] = useMemo(
    () => ({
      ...baseStyles,
      ...styles,
    }),
    [baseStyles, styles],
  );

  return combinedStyles;
};

export const useSelectTheme = <
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
  T extends SelectType,
>(): RenderSelectProps<OptionType, IsMulti, T>['selectProps']['theme'] => {
  const selectTheme: RenderSelectProps<
    OptionType,
    IsMulti,
    T
  >['selectProps']['theme'] = useCallback((baseTheme) => {
    return {
      ...baseTheme,
      colors: {
        ...baseTheme.colors,
        neutral50: 'var(--chakra-colors-feature-placeholder)', // placeholder text color
        neutral40: 'var(--chakra-colors-feature-placeholder)', // noOptionsMessage color
      },
      spacing: {
        ...baseTheme.spacing,
      },
    };
  }, []);

  return selectTheme;
};

export const useSelectSetRefsCallback = (
  forwardedRef: React.ForwardedRef<unknown>,
  inputRef: SelectWrapperProps['inputRef'],
): React.RefCallback<unknown> => {
  const setRefs = useCallback(
    (element) => {
      if (element !== null) {
        const inputElement =
          getInputElementFromSelectRefCallbackElement(element);

        if (inputElement instanceof HTMLInputElement) {
          inputRef.current = inputElement;
        }

        if (forwardedRef !== null) {
          if (isRefObject(forwardedRef)) {
            forwardedRef.current = element;
          } else {
            forwardedRef(element);
          }
        }
      }
    },
    [forwardedRef, inputRef],
  );

  return setRefs;
};

export const useSelectStateInternals = <
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
  T extends SelectType,
>(
  menuIsOpen: boolean | undefined,
  onBlur: RenderSelectProps<
    OptionType,
    IsMulti,
    T
  >['selectProps']['onBlur'] = noop,
  onFocus: RenderSelectProps<
    OptionType,
    IsMulti,
    T
  >['selectProps']['onFocus'] = () => noop,
): {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  doBlur: RenderSelectProps<OptionType, IsMulti, T>['selectProps']['onBlur'];
  doFocus: RenderSelectProps<OptionType, IsMulti, T>['selectProps']['onFocus'];
} => {
  const { isOpen, onClose, onOpen } = useDisclosure({ isOpen: menuIsOpen });

  const doBlur: RenderSelectProps<
    OptionType,
    IsMulti,
    T
  >['selectProps']['onBlur'] = useCallback(
    (blurEvent) => {
      onClose();
      onBlur(blurEvent);
    },
    [onClose, onBlur],
  );

  const doFocus: RenderSelectProps<
    OptionType,
    IsMulti,
    T
  >['selectProps']['onFocus'] = useCallback(
    (focusEvent) => {
      onOpen();
      onFocus(focusEvent);
    },
    [onOpen, onFocus],
  );

  return {
    isOpen,
    onClose,
    onOpen,
    doBlur,
    doFocus,
  };
};

type SuggestionRelatedContextValues = {
  hasSuggestions: boolean;
  hasAcceptedSuggestions: boolean;
  hasRejectedSuggestions: boolean;
};

export const useSelectInternals = <
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
  T extends SelectType,
>(
  rawBaseSelectProps: BaseSelectProps<OptionType, IsMulti, T>,
): {
  selectWrapperProps: SelectWrapperProps;
  renderSelectProps: RenderSelectProps<OptionType, IsMulti, T>;
} => {
  const { selectProps: rawSelectProps, customRef } = rawBaseSelectProps;
  const {
    onBlur,
    onFocus,
    size = 'md',
    components: componentsFromProps,
    styles: stylesFromProps,
    isMulti,
    isClearable,
    closeMenuOnSelect,
    menuIsOpen,
    menuPortalDestinationRef,
    options,
    value: selectedOptions,
    isSuggestionsEnabled = false,
    isPopoverOverlayEnabled = false,
    menuMatchReferenceWidth = true,
  } = rawSelectProps;

  const { isOpen, doBlur, doFocus } = useSelectStateInternals<
    OptionType,
    IsMulti,
    T
  >(menuIsOpen, onBlur, onFocus);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const selectStyles = useMultiStyleConfig('Select', { size }) as SelectStyles;

  const suggestionsProps: SuggestionRelatedContextValues = useMemo(() => {
    // NOTE: you have to pass in value to the select component for this
    //  to work properly with suggestions...it will not respond to internal
    //  updates to the value that are not persisted through props
    let hasSuggestions = false,
      hasAcceptedSuggestions = false,
      hasRejectedSuggestions = false;

    if (!isSuggestionsEnabled) {
      return {
        hasSuggestions,
        hasAcceptedSuggestions,
        hasRejectedSuggestions,
      };
    }

    const flatOptions =
      options?.flatMap((option) => option.options ?? option) ?? [];
    for (const option of flatOptions) {
      if (option.isSuggestion) {
        hasSuggestions = true;
        // now compute if the suggestion has been accepted or rejected
        if (selectedOptions && Array.isArray(selectedOptions)) {
          for (const selectedOption of selectedOptions) {
            if (selectedOption.value === option.value) {
              hasAcceptedSuggestions = true;
            } else if (!isMulti) {
              // only consider rejected if its not a multi selector
              hasRejectedSuggestions = true;
            }
          }
        } else if (selectedOptions) {
          if (selectedOptions.value === option.value) {
            hasAcceptedSuggestions = true;
          } else {
            hasRejectedSuggestions = true;
          }
        }
      }
    }

    return {
      hasSuggestions,
      hasAcceptedSuggestions,
      hasRejectedSuggestions,
    };
  }, [isMulti, isSuggestionsEnabled, options, selectedOptions]);

  const selectWrapperProps: SelectWrapperProps = useMemo(() => {
    return {
      isOpen,
      inputRef,
      selectStyles,
      matchWidth: menuMatchReferenceWidth,
      selectContextValue: {
        portalContainerRef: menuPortalDestinationRef,
        isPopoverOverlayEnabled,
        ...suggestionsProps,
      },
    };
  }, [
    isOpen,
    selectStyles,
    menuPortalDestinationRef,
    isPopoverOverlayEnabled,
    suggestionsProps,
    menuMatchReferenceWidth,
  ]);

  const ref = useSelectSetRefsCallback(customRef, inputRef);

  const components = useSelectComponents<OptionType, IsMulti, T>(
    componentsFromProps,
  );

  const styles = useSelectStyles<OptionType, IsMulti, T>(stylesFromProps);

  const theme = useSelectTheme<OptionType, IsMulti, T>();

  const alteredSelectProps = useMemo(() => {
    const reactSelectAltered = {
      ref,
      components,
      styles,
      theme,
      isClearable: isClearable ?? true,
      closeMenuOnSelect: closeMenuOnSelect || !isMulti,
      onBlur: doBlur,
      onFocus: doFocus,
      menuPortalTarget: undefined,
    };

    const combinedAltered = {
      ...reactSelectAltered,
      menuPortalDestinationRef: undefined,
    };

    return combinedAltered;
  }, [
    ref,
    components,
    styles,
    theme,
    isMulti,
    isClearable,
    closeMenuOnSelect,
    doBlur,
    doFocus,
  ]);

  const selectProps: RenderSelectProps<OptionType, IsMulti, T>['selectProps'] =
    useMemo(() => {
      const { menuMatchReferenceWidth: _, ...rawPropsForReactSelect } =
        rawSelectProps;
      const combinedSelectProps: RenderSelectProps<
        OptionType,
        IsMulti,
        T
      >['selectProps'] = {
        ...rawPropsForReactSelect,
        ...alteredSelectProps,
      };

      return combinedSelectProps;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawSelectProps, alteredSelectProps]);

  const renderSelectProps: RenderSelectProps<OptionType, IsMulti, T> = {
    ...rawBaseSelectProps,
    selectProps,
  };

  return { selectWrapperProps, renderSelectProps };
};
